import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useMemo } from 'react';

import {
  ScatterChart,
  ScatterChartProps,
} from '~/pages/Company/components/Inference/components/ScatterChart';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';
import { ceilToStep } from '~/utils/helpers';

import styles from './styles.module.scss';

interface Props {
  kpcaRo: number;
  kpcaFi: number;
  data: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['exit_score'];
  className?: string;
}

const Component: React.FC<Props> = ({ kpcaRo, kpcaFi, data, className }) => {
  const xAxisDomain = useMemo<ScatterChartProps['xAxisDomain']>(() => {
    return [Math.min(0, ceilToStep(kpcaRo - 0.2, 10)), Math.max(1.4, ceilToStep(kpcaRo + 0.2, 10))];
  }, [kpcaRo]);

  const yAxisDomain = useMemo<ScatterChartProps['yAxisDomain']>(() => {
    return [Math.min(-3, ceilToStep(kpcaFi - 1, 2)), Math.max(3, ceilToStep(kpcaFi + 1, 2))];
  }, [kpcaFi]);

  const areas = useMemo<ScatterChartProps['areas']>(() => {
    return [
      ...data.zones.green_zone.map(zone => ({
        x1: zone.curve[0][0],
        y1: zone.curve[0][1],
        x2: zone.curve[2][0],
        y2: zone.curve[2][1],
        type: 'success' as const,
      })),
      ...data.zones.yellow_zone.map(zone => ({
        x1: zone.curve[0][0],
        y1: zone.curve[0][1],
        x2: zone.curve[2][0],
        y2: zone.curve[2][1],
        type: 'danger' as const,
      })),
    ];
  }, [data.zones.green_zone, data.zones.yellow_zone]);

  const scatterChartData = useMemo<ScatterChartProps['data']>(() => {
    return data.data.map(data => ({ x: data.ro, y: data.fi }));
  }, [data.data]);

  const redDot = useMemo<ScatterChartProps['redDot']>(() => {
    return { x: kpcaRo, y: kpcaFi };
  }, [kpcaFi, kpcaRo]);

  return (
    <div className={clsx(styles.container, className)}>
      <Typography color="var(--text-3-color)" mb="27px" variant="subtitle1">
        Exit Zone
      </Typography>
      <ScatterChart
        areas={areas}
        data={scatterChartData}
        redDot={redDot}
        xAxisDomain={xAxisDomain}
        xAxisLabel="ro"
        xAxisTickCount={8}
        yAxisDomain={yAxisDomain}
        yAxisLabel="fi"
        yAxisTickCount={7}
      />
    </div>
  );
};

export const ExitZone = memo(Component);
